import { forwardRef, type ButtonHTMLAttributes, type CSSProperties } from 'react';
import { colors, fontFamily, radius } from './tokens';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
};

const base: CSSProperties = {
  fontFamily: fontFamily.sans,
  fontWeight: 500,
  border: '1px solid transparent',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  whiteSpace: 'nowrap',
  transition: 'background 0.15s, color 0.15s, border-color 0.15s',
};

const sizes: Record<ButtonSize, CSSProperties> = {
  sm: { fontSize: 12, padding: '6px 10px', borderRadius: radius.xs },
  md: { fontSize: 14, padding: '10px 16px', borderRadius: radius.xs },
  lg: { fontSize: 14, padding: '14px 22px', borderRadius: 10 },
};

const variants: Record<ButtonVariant, CSSProperties> = {
  primary: { background: colors.ink, color: colors.surface },
  secondary: { background: colors.surface, color: colors.ink, borderColor: colors.line2 },
  ghost: { background: 'transparent', color: colors.ink2 },
  danger: { background: colors.bad, color: colors.surface },
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { variant = 'primary', size = 'md', style, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      style={{ ...base, ...sizes[size], ...variants[variant], ...style }}
      {...rest}
    />
  );
});
